'use client'

import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

const SEARCH_SUGGESTIONS = [
  'CBC Test',
  'Thyroid Profile',
  'Full Body Checkup',
  'Vitamin D Test',
  'Lipid Profile',
  'Liver Function Test',
  'HbA1c Test',
  'Iron Studies',
]

const TYPING_SPEED = 80
const DELETING_SPEED = 40
const PAUSE_DURATION = 1500

interface CommonSearchInputProps {
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  showCyclicText?: boolean
  containerClassName?: string
  className?: string
  autoFocus?: boolean
  readOnly?: boolean
  onClick?: () => void
  onClear?: () => void
}

const CommonSearchInput = React.forwardRef<
  HTMLInputElement,
  CommonSearchInputProps
>(
  (
    {
      value,
      onChange,
      placeholder = 'Search for lab tests',
      showCyclicText = false,
      containerClassName,
      className,
      autoFocus = false,
      readOnly = false,
      onClick,
      onClear,
    },
    ref
  ) => {
    // Animated placeholder
    const [displayText, setDisplayText] = useState('')
    const [isDeleting, setIsDeleting] = useState(false)
    const currentIndexRef = useRef(0)
    const charIndexRef = useRef(0)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
      if (!showCyclicText) return

      const tick = () => {
        const currentWord = SEARCH_SUGGESTIONS[currentIndexRef.current]

        if (!isDeleting) {
          charIndexRef.current++
          setDisplayText(currentWord.slice(0, charIndexRef.current))

          if (charIndexRef.current === currentWord.length) {
            timeoutRef.current = setTimeout(() => {
              setIsDeleting(true)
            }, PAUSE_DURATION)
            return
          }
          timeoutRef.current = setTimeout(tick, TYPING_SPEED)
        } else {
          charIndexRef.current--
          setDisplayText(currentWord.slice(0, charIndexRef.current))

          if (charIndexRef.current === 0) {
            setIsDeleting(false)
            currentIndexRef.current =
              (currentIndexRef.current + 1) % SEARCH_SUGGESTIONS.length
            timeoutRef.current = setTimeout(tick, TYPING_SPEED)
            return
          }
          timeoutRef.current = setTimeout(tick, DELETING_SPEED)
        }
      }

      timeoutRef.current = setTimeout(
        tick,
        isDeleting ? DELETING_SPEED : TYPING_SPEED
      )

      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
      }
    }, [isDeleting, showCyclicText])

    return (
      <div
        onClick={readOnly ? onClick : undefined}
        className={cn(
          'flex items-center h-10 border rounded-md bg-gray-50 focus-within:bg-white px-3 gap-2 transition-colors',
          'border-gray-300 focus-within:border-primary-300',
          readOnly && 'cursor-pointer',
          containerClassName
        )}
      >
        {/* Search icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-gray-500 flex-shrink-0"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>

        {/* Input */}
        <div className="flex-1 min-w-0">
          {showCyclicText && !value ? (
            <span className="text-body-sm text-gray-800 truncate block whitespace-nowrap">
              Search for &quot;{displayText}&quot;
            </span>
          ) : (
            <input
              ref={ref}
              type="text"
              data-slot="input-group-control"
              autoComplete="off"
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              autoFocus={autoFocus}
              readOnly={readOnly}
              className={cn(
                'w-full bg-transparent text-body-sm text-gray-800 placeholder:text-gray-400 placeholder:font-normal outline-none border-none p-0 shadow-none',
                readOnly && 'cursor-pointer',
                className
              )}
            />
          )}
        </div>

        {/* Clear button */}
        {onClear && value && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear search"
            className="flex-shrink-0 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    )
  }
)

CommonSearchInput.displayName = 'CommonSearchInput'

export default CommonSearchInput
